import { useState } from "react";
import Screen from "../../components/Screen";
import { PrimaryBtn, GhostBtn } from "../../components/Buttons";
import Notice from "../../components/Notice";
import { infoIcon } from "../../components/Ico";

interface NpHomePhoneProps {
  /** 送出已填的家用電話（可多組，已過濾空白/非數字）；上層依序打真後端逐一比對並決定導頁。 */
  onSubmit: (landlines: string[]) => void;
  /** 上層正在依序比對中（呼叫真後端）→ 禁用送出按鈕，避免重複送出。 */
  matching?: boolean;
  /** 瓦斯行已知（傳單·有指定）→ 文案改為「找出您的既有資料」而非「尋找瓦斯行」 */
  supplierKnown?: boolean;
  /** 省略＝這一頁沒有上一步（流程第一頁）→ 不顯示返回鍵。由 Onboarding 的導航堆疊 canGoBack 決定。 */
  onBack?: () => void;
}

/** 用叫瓦斯登記的家用電話（可多組）比對：無指定 → 尋找所屬瓦斯行；有指定 → 找出既有客戶資料。
 *  實際比對交由上層（index.tsx）依序呼叫真後端 match/by-landline 或 match/suppliers-by-landline。 */
const NpHomePhone = ({ onSubmit, matching, supplierKnown, onBack }: NpHomePhoneProps) => {
  const [phones, setPhones] = useState<string[]>([""]);
  const [showNoPhone, setShowNoPhone] = useState(false);
  const update = (i: number, v: string) => {
    const n = [...phones];
    n[i] = v;
    setPhones(n);
  };
  const add = () => setPhones([...phones, ""]);
  const remove = (i: number) => setPhones(phones.filter((_, idx) => idx !== i));

  const cleaned = phones.map((p) => p.replace(/\D/g, "")).filter(Boolean);
  const canSubmit = cleaned.length > 0 && !matching;

  const submit = () => {
    if (matching) return;
    onSubmit(cleaned);
  };

  return (
    <Screen
      title="輸入家用電話"
      subtitle={supplierKnown ? "用您叫瓦斯登記的家用電話找出您的既有資料" : "用您叫瓦斯登記的家用電話尋找瓦斯行"}
      onBack={onBack}
      footer={
        <>
          <PrimaryBtn active={cleaned.length > 0} busy={matching} busyText="比對中…" onClick={submit}>確定，下一步</PrimaryBtn>
          <GhostBtn onClick={() => setShowNoPhone(true)}>目前沒有家用電話</GhostBtn>
        </>
      }
    >
      <div className="text-[13px] text-[#475569] font-semibold mb-2">家用電話（可填多組：市話 / 另一支手機 / 家人的）</div>
      <div className="flex flex-col gap-2.5">
        {phones.map((p, i) => (
          <div key={i} className="flex gap-2.5 items-center">
            <input
              value={p}
              onChange={(e) => update(i, e.target.value)}
              inputMode="tel"
              placeholder="09xx xxx xxx 或 04-xxxx-xxxx"
              className="flex-1 h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[16px] outline-none box-border text-[#333]"
            />
            {phones.length > 1 && (
              <button onClick={() => remove(i)} className="w-9 h-9 rounded-full bg-[#F1F5F9] text-[#94A3B8] text-lg shrink-0 cursor-pointer border-none">
                ×
              </button>
            )}
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-2.5 text-[13px] font-semibold text-[#2196f3] bg-transparent border-none cursor-pointer p-0">
        ＋ 再填一組電話
      </button>
      <div className="mt-[18px]">
        <Notice tone="blue" icon={infoIcon}>{supplierKnown ? "用這些電話為您比對既有客戶資料。" : "用這些電話為您比對所屬瓦斯行。"}</Notice>
      </div>

      {/* 「目前沒有」確認 modal：提醒可能比對不到 */}
      {showNoPhone && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center px-8">
          <div className="absolute inset-0 bg-[#0f1a35]/45 onb-overlay-fade" onClick={() => setShowNoPhone(false)} />
          <div className="relative w-full max-w-[320px] bg-white rounded-[20px] p-6 onb-rise">
            <div className="w-14 h-14 rounded-full bg-[#FEF3C7] flex items-center justify-center mx-auto mb-3">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l9 16H3L12 3z" stroke="#F59E0B" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="M12 10v4M12 17h.01" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <div className="text-[17px] font-extrabold text-[#0f1a35] text-center">目前沒有家用電話？</div>
            <div className="text-[13px] text-[#64748B] text-center mt-2 mb-5 leading-[1.6]">
              沒有可對應的電話，<b className="text-[#B45309]">可能找不到</b>{supplierKnown ? "您的既有資料，將改用地址繼續。" : "您所屬的瓦斯行，將改用地址為您繼續尋找。"}
            </div>
            <div className="flex flex-col gap-2.5">
              <PrimaryBtn onClick={() => onSubmit([])}>仍要繼續</PrimaryBtn>
              <GhostBtn onClick={() => setShowNoPhone(false)}>返回填寫</GhostBtn>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
};

export default NpHomePhone;
